import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { notification } from "antd";
import Cookies from "js-cookie";
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import moment from "moment";

function Login() {
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Clear old session
    Object.keys(Cookies.get()).forEach((c) => Cookies.remove(c));
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Invalid email format";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post("doctor/LoginWeb", {
        email,
        password,
        module: "doctor",
      });

      if (response.data.status === 1) {
        const userData = response.data.data.find(item => item.user_type_id === 1);

        if (!userData) {
          notification.error({ message: "Unauthorized user type" });
          return;
        }

        const expiryTime = new Date(Date.now() + 1 * 60 * 60 * 1000); // 1 hour

        Cookies.set("tokenHeader", response.data.token, { sameSite: "Strict" });
        Cookies.set("tokenExpiry", expiryTime.toISOString(), { sameSite: "Strict" });
        Cookies.set("LoginData", JSON.stringify(userData), { expires: 1 });
        Cookies.set("Doctor_name", userData.user_name, { expires: 1 });
        Cookies.set("Doctor_id", userData.vendor_id, { expires: 1 });
        Cookies.set("Doctor_vendor_type_id", userData.user_type_id, { expires: 1 });
        Cookies.set("Date", moment(new Date()).format("YYYY-MM-DD"), { expires: 1 });

        // Fetch clinic info
        await fetchDoctorClinics(userData.vendor_id);

        notification.success({ message: "Login Successful" });
        history.push("/dashboard");
      } else {
        notification.error({ message: response.data.msg || "Invalid credentials" });
      }
    } catch (err) {
      console.error(err);
      notification.error({ message: "Login failed. Please check your credentials." });
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorClinics = async (doctorId) => {
    try {
      const response = await axiosInstance.post("doctor/getDoctorClinics", {
        doctorId: doctorId,
      });
      if (response.data.data?.length > 0) {
        const clinic = response.data.data[0];
        Cookies.set("Clinic_id", clinic.clinicId, { expires: 1 });
        Cookies.set("clinic_name", clinic.clinicName, { expires: 1 });
        if (clinic.pharmacyId) {
          Cookies.set("pharmacy_id", clinic.pharmacyId, { expires: 1 });
          localStorage.setItem("pharmacy_id", clinic.pharmacyId);
        }
      }
    } catch (err) {
      console.error("Failed to fetch doctor clinics", err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
      <div className="max-w-4xl w-full bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* Left Side - Brand Context */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-800 p-8 sm:p-10 md:p-12 text-white flex flex-col justify-center items-center text-center relative">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 font-sans text-white leading-tight">Welcome Back, Doctor!</h2>
          <p className="text-teal-100 text-sm sm:text-base font-light max-w-xs opacity-90 leading-relaxed">
            Access your schedules, patient records, and manage your practice effortlessly.
          </p>
          <div className="hidden md:block mt-8 space-y-3 w-full max-w-[180px]">
            <div className="h-1 bg-white/20 rounded-full w-full"></div>
            <div className="h-1 bg-white/20 rounded-full w-2/3 mx-auto"></div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="p-8 sm:p-10 md:p-12 flex flex-col justify-center bg-white">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">Sign In</h1>
            <p className="text-sm text-gray-500">Enter your credentials to access your dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-teal-600 transition-colors">
                  <HiOutlineMail className="h-5 w-5" />
                </div>
                <input
                  type="email"
                  className={`block w-full pl-10 pr-4 py-3 border-2 ${errors.email ? 'border-red-500' : 'border-gray-100'} rounded-xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 transition-all outline-none text-gray-800 bg-white placeholder:text-gray-400 text-sm`}
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs text-red-500 font-semibold ml-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Password</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-teal-600 transition-colors">
                  <HiOutlineLockClosed className="h-5 w-5" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`block w-full pl-10 pr-12 py-3 border-2 ${errors.password ? 'border-red-500' : 'border-gray-100'} rounded-xl focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 transition-all outline-none text-gray-800 bg-white placeholder:text-gray-400 text-sm`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-teal-600 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <HiOutlineEyeOff size={18} /> : <HiOutlineEye size={18} />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-xs text-red-500 font-semibold ml-1">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between text-sm pt-1">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer" />
                <span className="ml-2 text-gray-600 text-xs sm:text-sm">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => history.push('/forgot')}
                className="text-xs sm:text-sm font-semibold text-teal-600 hover:text-teal-700 transition-colors border-none bg-transparent cursor-pointer"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 px-6 rounded-xl text-white font-bold text-base shadow-lg transform transition-all active:scale-[0.98] border-none cursor-pointer mt-2 ${loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 hover:shadow-teal-100 shadow-teal-500/30 hover:-translate-y-0.5'
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : "Sign In"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs sm:text-sm text-gray-500">
            Having trouble? <span className="text-teal-600 font-semibold cursor-pointer">Contact Tech Support</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
